/**
 * Posiciona el cursor en el inicio de un elemento dado
 */
export const colocarCursorEnElemento = (editorEl, el, savedSelectionRef) => {
  try {
    if (!editorEl || !el) return;
    editorEl.focus();
    const sel = window.getSelection();
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(true);
    if (sel) {
      sel.removeAllRanges();
      sel.addRange(range);
    }
    if (savedSelectionRef) {
      savedSelectionRef.current = range.cloneRange();
    }
  } catch (e) {}
};

/**
 * Obtiene la tabla, fila y celda actuales bajo el cursor o desde la selección guardada
 */
export const obtenerContextoTabla = (editorEl, savedSelectionRef, activeTableContextRef) => {
  const sel = window.getSelection();
  let nodo = null;
  if (sel && sel.rangeCount > 0) {
    nodo = sel.getRangeAt(0).startContainer;
  } else if (savedSelectionRef?.current) {
    nodo = savedSelectionRef.current.startContainer;
  }

  let celda = null;
  let fila = null;
  let tabla = null;

  while (nodo && nodo !== editorEl) {
    if (!celda && (nodo.nodeName === 'TD' || nodo.nodeName === 'TH')) {
      celda = nodo;
    }
    if (!fila && nodo.nodeName === 'TR') {
      fila = nodo;
    }
    if (nodo.nodeName === 'TABLE') {
      tabla = nodo;
      break;
    }
    nodo = nodo.parentNode;
  }

  // Fallback con el ref registrado previamente si el foco se perdió temporalmente
  if (!tabla && activeTableContextRef?.current?.tabla && editorEl && editorEl.contains(activeTableContextRef.current.tabla)) {
    tabla = activeTableContextRef.current.tabla;
    fila = activeTableContextRef.current.fila;
    celda = activeTableContextRef.current.celda;
  }

  return { tabla, fila, celda };
};

/**
 * Aplica estilos de bordes a la tabla actual
 */
export const cambiarEstiloTabla = (
  estilo,
  contexto,
  setEstiloTablaActual,
  notificarCambio
) => {
  const { tabla } = contexto;
  if (!tabla) return;

  tabla.classList.remove('tabla-sin-bordes', 'tabla-con-bordes', 'tabla-lineas-horizontales');
  const celdas = tabla.querySelectorAll('td, th');

  if (estilo === 'sin-bordes') {
    tabla.classList.add('tabla-sin-bordes');
    tabla.setAttribute('border', '0');
    tabla.style.border = 'none';
    tabla.style.borderCollapse = 'collapse';
    celdas.forEach((c) => {
      c.style.border = 'none';
    });
  } else if (estilo === 'horizontales') {
    tabla.classList.add('tabla-lineas-horizontales');
    tabla.setAttribute('border', '0');
    tabla.style.border = 'none';
    tabla.style.borderTop = '2px solid #718096';
    tabla.style.borderBottom = '2px solid #718096';
    tabla.style.borderCollapse = 'collapse';
    celdas.forEach((c) => {
      c.style.borderLeft = 'none';
      c.style.borderRight = 'none';
      c.style.borderTop = '1px solid #e2e8f0';
      c.style.borderBottom = '1px solid #e2e8f0';
    });
  } else {
    // con-bordes
    tabla.classList.add('tabla-con-bordes');
    tabla.setAttribute('border', '1');
    tabla.style.border = '1px solid #718096';
    tabla.style.borderCollapse = 'collapse';
    celdas.forEach((c) => {
      c.style.border = '1px solid #718096';
    });
  }

  if (setEstiloTablaActual) {
    setEstiloTablaActual(estilo);
  }
  if (notificarCambio) {
    notificarCambio();
  }
};

/**
 * Inserta una nueva fila arriba o abajo de la fila actual
 */
export const insertarFila = (
  posicion = 'abajo',
  contexto,
  activeTableContextRef,
  editorEl,
  savedSelectionRef,
  notificarCambio
) => {
  const { tabla, fila } = contexto;
  if (!tabla) return;

  const targetRow = fila || tabla.querySelector('tr');
  if (!targetRow) return;

  const numCols = targetRow.cells.length;
  const esSinBordes = tabla.classList.contains('tabla-sin-bordes');
  const esHorizontales = tabla.classList.contains('tabla-lineas-horizontales');

  const nuevaFila = document.createElement('tr');
  for (let i = 0; i < numCols; i++) {
    const td = document.createElement('td');
    td.innerHTML = '<br>';
    td.style.padding = '8px 12px';
    td.style.verticalAlign = 'top';

    if (esSinBordes) {
      td.style.border = 'none';
    } else if (esHorizontales) {
      td.style.borderTop = '1px solid #e2e8f0';
      td.style.borderBottom = '1px solid #e2e8f0';
      td.style.borderLeft = 'none';
      td.style.borderRight = 'none';
    } else {
      td.style.border = '1px solid #718096';
    }

    nuevaFila.appendChild(td);
  }

  if (posicion === 'arriba') {
    targetRow.parentNode.insertBefore(nuevaFila, targetRow);
  } else {
    targetRow.parentNode.insertBefore(nuevaFila, targetRow.nextSibling);
  }

  if (nuevaFila.cells[0]) {
    colocarCursorEnElemento(editorEl, nuevaFila.cells[0], savedSelectionRef);
    if (activeTableContextRef) {
      activeTableContextRef.current = {
        tabla,
        fila: nuevaFila,
        celda: nuevaFila.cells[0],
      };
    }
  }

  if (notificarCambio) {
    notificarCambio();
  }
};

/**
 * Elimina la fila actual de la tabla
 */
export const eliminarFila = (
  contexto,
  activeTableContextRef,
  editorEl,
  savedSelectionRef,
  setEstaEnTabla,
  setEstiloTablaActual,
  notificarCambio
) => {
  const { tabla, fila } = contexto;
  if (!tabla) return;

  const filaAEliminar = fila || tabla.querySelector('tr');
  if (!filaAEliminar) return;

  if (tabla.rows.length <= 1) {
    tabla.remove();
    if (activeTableContextRef) {
      activeTableContextRef.current = { tabla: null, fila: null, celda: null };
    }
    if (setEstaEnTabla) setEstaEnTabla(false);
    if (setEstiloTablaActual) setEstiloTablaActual(null);
  } else {
    const siguienteFila = filaAEliminar.nextElementSibling || filaAEliminar.previousElementSibling;
    const celdaDestino = siguienteFila ? (siguienteFila.cells[0] || siguienteFila) : null;

    filaAEliminar.remove();

    if (celdaDestino) {
      colocarCursorEnElemento(editorEl, celdaDestino, savedSelectionRef);
      if (activeTableContextRef) {
        activeTableContextRef.current = {
          tabla,
          fila: siguienteFila,
          celda: celdaDestino,
        };
      }
    }
  }

  if (notificarCambio) {
    notificarCambio();
  }
};

/**
 * Inserta una columna a la izquierda o derecha de la celda actual
 */
export const insertarColumna = (
  posicion = 'derecha',
  contexto,
  activeTableContextRef,
  editorEl,
  savedSelectionRef,
  notificarCambio
) => {
  const { tabla, celda } = contexto;
  if (!tabla) return;

  const colIndex = celda ? celda.cellIndex : (tabla.rows[0]?.cells.length - 1 || 0);
  const esSinBordes = tabla.classList.contains('tabla-sin-bordes');
  const esHorizontales = tabla.classList.contains('tabla-lineas-horizontales');

  let primeraCeldaNueva = null;

  Array.from(tabla.rows).forEach((row, rIdx) => {
    const refCell = row.cells[colIndex] || row.cells[row.cells.length - 1];
    const esTh = refCell && refCell.nodeName === 'TH';
    const nuevaCelda = document.createElement(esTh ? 'th' : 'td');
    nuevaCelda.innerHTML = '<br>';
    nuevaCelda.style.padding = '8px 12px';
    nuevaCelda.style.verticalAlign = 'top';

    if (esSinBordes) {
      nuevaCelda.style.border = 'none';
    } else if (esHorizontales) {
      nuevaCelda.style.borderTop = '1px solid #e2e8f0';
      nuevaCelda.style.borderBottom = '1px solid #e2e8f0';
      nuevaCelda.style.borderLeft = 'none';
      nuevaCelda.style.borderRight = 'none';
    } else {
      nuevaCelda.style.border = '1px solid #718096';
    }

    if (posicion === 'izquierda' && refCell) {
      row.insertBefore(nuevaCelda, refCell);
    } else if (refCell && refCell.nextSibling) {
      row.insertBefore(nuevaCelda, refCell.nextSibling);
    } else {
      row.appendChild(nuevaCelda);
    }

    if (rIdx === 0) {
      primeraCeldaNueva = nuevaCelda;
    }
  });

  if (primeraCeldaNueva) {
    colocarCursorEnElemento(editorEl, primeraCeldaNueva, savedSelectionRef);
    if (activeTableContextRef) {
      activeTableContextRef.current = {
        tabla,
        fila: tabla.rows[0],
        celda: primeraCeldaNueva,
      };
    }
  }

  if (notificarCambio) {
    notificarCambio();
  }
};

/**
 * Elimina la columna actual de la tabla
 */
export const eliminarColumna = (
  contexto,
  activeTableContextRef,
  editorEl,
  savedSelectionRef,
  setEstaEnTabla,
  setEstiloTablaActual,
  notificarCambio
) => {
  const { tabla, celda } = contexto;
  if (!tabla) return;

  const colIndex = celda ? celda.cellIndex : (tabla.rows[0]?.cells.length - 1 || 0);
  const numCols = tabla.rows[0]?.cells.length || 0;

  if (numCols <= 1) {
    tabla.remove();
    if (activeTableContextRef) {
      activeTableContextRef.current = { tabla: null, fila: null, celda: null };
    }
    if (setEstaEnTabla) setEstaEnTabla(false);
    if (setEstiloTablaActual) setEstiloTablaActual(null);
  } else {
    Array.from(tabla.rows).forEach((row) => {
      if (row.cells[colIndex]) {
        row.deleteCell(colIndex);
      }
    });

    const nuevoColIndex = Math.min(colIndex, (tabla.rows[0]?.cells.length - 1) || 0);
    const celdaDestino = tabla.rows[0]?.cells[nuevoColIndex];
    if (celdaDestino) {
      colocarCursorEnElemento(editorEl, celdaDestino, savedSelectionRef);
      if (activeTableContextRef) {
        activeTableContextRef.current = {
          tabla,
          fila: tabla.rows[0],
          celda: celdaDestino,
        };
      }
    }
  }

  if (notificarCambio) {
    notificarCambio();
  }
};

/**
 * Elimina la tabla completa
 */
export const eliminarTabla = (
  contexto,
  activeTableContextRef,
  setEstaEnTabla,
  setEstiloTablaActual,
  notificarCambio
) => {
  const { tabla } = contexto;
  if (tabla) {
    tabla.remove();
    if (activeTableContextRef) {
      activeTableContextRef.current = { tabla: null, fila: null, celda: null };
    }
    if (setEstaEnTabla) setEstaEnTabla(false);
    if (setEstiloTablaActual) setEstiloTablaActual(null);
    if (notificarCambio) notificarCambio();
  }
};

/**
 * Genera el código HTML para una nueva tabla según la configuración seleccionada
 */
export const generarHtmlTabla = ({ filas = 3, columnas = 2, estilo = 'con-bordes', conEncabezado = true }) => {
  const numFilas = Math.min(30, Math.max(1, Number.parseInt(filas, 10) || 3));
  const numColumnas = Math.min(10, Math.max(1, Number.parseInt(columnas, 10) || 2));

  let claseEstilo = 'tabla-con-bordes';
  let borderAttr = '1';
  let tableStyle = 'width: 100%; border-collapse: collapse; margin: 12px 0; border: 1px solid #718096;';
  let cellStyle = 'border: 1px solid #718096; padding: 8px 12px; vertical-align: top;';

  if (estilo === 'sin-bordes') {
    claseEstilo = 'tabla-sin-bordes';
    borderAttr = '0';
    tableStyle = 'width: 100%; border-collapse: collapse; margin: 12px 0; border: none;';
    cellStyle = 'border: none; padding: 8px 12px; vertical-align: top;';
  } else if (estilo === 'horizontales') {
    claseEstilo = 'tabla-lineas-horizontales';
    borderAttr = '0';
    tableStyle = 'width: 100%; border-collapse: collapse; margin: 12px 0; border-top: 2px solid #718096; border-bottom: 2px solid #718096; border-left: none; border-right: none;';
    cellStyle = 'border-top: 1px solid #e2e8f0; border-bottom: 1px solid #e2e8f0; border-left: none; border-right: none; padding: 8px 12px; vertical-align: top;';
  }

  let html = `<table class="${claseEstilo}" border="${borderAttr}" style="${tableStyle}"><tbody>`;
  for (let f = 0; f < numFilas; f++) {
    html += '<tr>';
    for (let c = 0; c < numColumnas; c++) {
      if (f === 0 && conEncabezado && estilo !== 'sin-bordes') {
        html += `<th style="${cellStyle} background-color: #f8fafc; font-weight: bold; text-align: left;">Encabezado ${c + 1}</th>`;
      } else if (estilo === 'sin-bordes') {
        if (f === 0) {
          html += `<td style="${cellStyle} text-align: center; padding-top: 35px;">______________________________<br><b>Firma ${c + 1}</b></td>`;
        } else {
          html += `<td style="${cellStyle} text-align: center;">Aclaración / Cargo</td>`;
        }
      } else {
        html += `<td style="${cellStyle}">Dato</td>`;
      }
    }
    html += '</tr>';
  }
  html += '</tbody></table><p><br></p>';

  return html;
};
